/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

import { ResponderMessage } from "../../clients/routerClient";
import { Logger } from "../../clients/logger";
import { Globals } from "../../common";
import { System } from "../../common/system";
import { StandardError } from "../../types";
import { BaseService } from "../baseService";

Logger.start();

type RegisteredHotkeys = Record<string, any>;
declare let fin: NonNullable<typeof Globals.fin>;

/**
 * The HotkeyService is responsible for registering and unregistering hotkeys via the hotkey client. It checks key
 * presses received from assimilation, and checks if those key presses match and key combinations registered as hotkeys
 * by hotkey clients. If it does, it sends out a router message including all windows registered with that component.
 */
class HotkeyService extends BaseService {
	registeredHotkeys: RegisteredHotkeys;

	keysDown: Record<string, any>;

	constructor() {
		// FIXME(Terry) should be hotkeyService (with no "s") in order to match HotkeyClient
		super({
			startupDependencies: {},
			shutdownDependencies: {
				services: ["windowService"],
			},
			name: "hotkeysService",
		});
		this.registeredHotkeys = {};
		this.keysDown = {}; // currently pressed keys

		this.bindCorrectContext();
		this.onBaseServiceReady((done: () => void) => {
			this.doInit(done);
		});

		this.start();
	}

	bindCorrectContext() {
		this.initialize = this.initialize.bind(this);
		this.createRouterEndpoints = this.createRouterEndpoints.bind(this);
		this.getRegisteredHotkeys = this.getRegisteredHotkeys.bind(this);
		this.registerGlobalHotkey = this.registerGlobalHotkey.bind(this);
		this.removeAllHotkeysForWindow = this.removeAllHotkeysForWindow.bind(this);
		this.unregisterGlobalHotkey = this.unregisterGlobalHotkey.bind(this);
	}

	/**
	 * Just adds router endpoints
	 *
	 * @param {*} cb
	 */
	createRouterEndpoints(cb: Function) {
		this.RouterClient.addResponder("hotkeysService.registerGlobalHotkey", this.registerGlobalHotkey);

		this.RouterClient.addResponder("hotkeysService.unregisterGlobalHotkey", this.unregisterGlobalHotkey);

		this.RouterClient.addResponder("hotkeysService.removeAllHotkeysForWindow", this.removeAllHotkeysForWindow);

		this.RouterClient.addListener("hotkeysService.getRegisteredHotkeys", this.getRegisteredHotkeys);

		Logger.system.info("Added listeners for hotkeysService");

		cb();
	}

	/**
	 * Initializes a hotkey service
	 */
	doInit(cb: Function) {
		this.createRouterEndpoints(cb);
		System.addEventListener("session-changed", () => {
			this.keysDown = {};
		});
	}

	/**
	 * Passes back the registered keys to the caller
	 *
	 * @param {*} error
	 * @param {*} response
	 */
	getRegisteredHotkeys(error: StandardError) {
		if (error) {
			Logger.system.log(`requestHotkeys error: ${error}`);
		} else {
			Logger.system.log("currently registered Hotkeys: ", this.registeredHotkeys);
		}
		return this.registeredHotkeys;
	}

	/**
	 * This function registers global hotkeys - ones that can be triggered regardless of what window is in focus.
	 * Registration occurs in this.registeredHotkeys, which consists of key-value pairs where the key is the key
	 * combination for the hotkey, and the value is all windows that have registered a key combination with that hotkey
	 * @param {*} error
	 * @param {*} message
	 */
	registerGlobalHotkey(error: StandardError, message: ResponderMessage | undefined) {
		if (!message) {
			Logger.system.error("No responder message passed to HotkeyService.registerGlobalHotkey");
			return;
		}
		const origin = message.data.windowName;
		const { keys } = message.data;
		Logger.system.log("hotkeysService.registerGlobalHotkey, hotkey: ", keys, " origin: ", origin);
		if (!this.registeredHotkeys[keys]) {
			this.registeredHotkeys[keys] = [origin];
			fin.desktop.System.GlobalShortcut.register(keys, () => {
				this.RouterClient.transmit("HotkeyTriggered", {
					windows: this.registeredHotkeys[keys],
					keys,
				});
			});
		} else {
			this.registeredHotkeys[keys].push(origin);
		}

		message.sendQueryResponse(null, "success");
	}

	/**
	 * Unregisters a global hotkey - removes the window id from this.registeredHotkeys[keys] (or deletes
	 * this.registeredHotkeys[keys] if it's the only one)
	 *
	 * @param err
	 * @param message
	 * @returns
	 */
	unregisterGlobalHotkey(err: StandardError, message: ResponderMessage | undefined) {
		if (!message) {
			Logger.system.error("No responder message passed to HotkeyService.unregisterGlobalHotkey");
			return;
		}
		let error = err;
		const { keys } = message.data;
		let windowsListening = this.registeredHotkeys[keys];
		Logger.system.log("hotkeysService.registerGlobalHotkey, hotkey: ", keys);
		if (windowsListening) {
			// note that "keys" is already a string - if any hotkeys exist for this key combination
			if (windowsListening.includes(message.data.windowName)) {
				windowsListening = windowsListening.splice(windowsListening.indexOf(message.data.windowName), 1);
				if (this.registeredHotkeys[keys].length === 0) {
					delete this.registeredHotkeys[keys];
					fin.desktop.System.GlobalShortcut.unregister(keys);
				}
			} else {
				error = "Attempt to remove hotkey failed - this component had not registered this hotkey with the service";
			}
		} else {
			error = "Attempt to remove hotkey failed - this component had not registered this hotkey with the service";
		}
		if (error) {
			Logger.system.error(error);
		}
		message.sendQueryResponse(error, error ? null : "success");
	}

	/**
	 * Removes all hotkeys for a window
	 *
	 * @param {*} err
	 * @param {*} message
	 */
	removeAllHotkeysForWindow(err: StandardError, message: ResponderMessage | undefined) {
		if (!message) {
			Logger.system.error("No responder message passed to HotkeyService.removeAllHotkeysForWindow");
			return;
		}
		Logger.system.info("Removing all hotkeys for ", message.data.windowName);
		const hotkeyCombinations = Object.keys(this.registeredHotkeys);
		hotkeyCombinations.forEach((combo) => {
			const windowNames = this.registeredHotkeys[combo];
			let i = windowNames.length;
			// GO through all of the window names. if any match the window trying to unregister, remove it from the array.
			// windows can have multiple listeners for a single hotkey combination; this will handle that.
			while (i--) {
				if (windowNames[i] === message.data.windowName) {
					windowNames.splice(i, 1);
				}
			}
		});
		message.sendQueryResponse(null, "success");
	}
}

export default new HotkeyService();
